import '../home/home.scss'
import '../onboarding/onboarding.scss'
import '../../components/common/alertFetchSpinner/alertFetchSpinner.scss'
import { Box, Tab, Modal, Typography, Paper, InputAdornment, Button, TextField, Checkbox, FormControlLabel, IconButton, TablePagination } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { FileUploader } from 'react-drag-drop-files'
import { useTranslation } from 'react-i18next'
import { RiskLevel } from '../../components/utilities/RiskLevel'
import { SearchOutlined, Delete, Visibility, FilterList } from '@mui/icons-material'
import Spinner from '../../components/common/spinner/spinner'
import LetterAvatar from '../../components/common/userDetails/LetterAvatar'
import MyDatePicker from '../../components/utilities/MyDatePicker'
import { updateKybListFilters, deleteSelectedItems } from '../../features/kybLists/kybListsSlice'
import { useKybList } from '../pages-hooks/useKybList'
import { FilterModal } from '../../components/utilities/FilterModal'
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro'
import CustomLoadingOverlay from '../../components/common/datagrid/CustomLoadingOverlay'
import CustomColumnMenu from '../../components/common/datagrid/CustomColumnMenu'
import dayjs from 'dayjs'
import { useUpdateKybListMutation, useDeleteKybListItemsMutation } from '../../services/guenoApi'
import toaster from '../../toaster'

const FILE_TYPES = ['XLSX']

const KybLists = () => {
   const { tab } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const queryState = useSelector((state) => state.kybList.filters)
  const list = useSelector((state) => state.kybList.list)
  const selectedItems = useSelector((state) => state.kybList.selectedItems || [])

  useEffect(() => {
    if (selectedItems.length && list?.data) {
      const validIds = new Set(list.data.map(item => item._id || item.id))
      const filtered = selectedItems.filter(id => validIds.has(id))
      if (filtered.length !== selectedItems.length) {
        dispatch(deleteSelectedItems(filtered))
      }
    }
  }, [list?.data, selectedItems, dispatch])

  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [toDate, setToDate] = useState(null)
  const [actualTab, setActualTab] = useState('CNPJ')
  const [id, setId] = useState(queryState.id)
  const [status, setStatus] = useState(queryState.status)
  const [name, setName] = useState(queryState.name)
  const [selectedDate, setSelectedDate] = useState(queryState.fromDate)
  const [documentNumber, setDocumentNumber] = useState(queryState.documentNumber)
  const [modalOpen, setModalOpen] = useState(false)
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [file, setFile] = useState('')
  const [replaceLists, setReplaceLists] = useState(false)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState('')
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('')
  const { t } = useTranslation(['common', 'messages'])

  useKybList(setIsLoading, isLoading, actualTab)

  const [updateKybList] = useUpdateKybListMutation()
  const [deleteKybListItems, { isLoading: isDeleting }] = useDeleteKybListItemsMutation()
  const apiRef = useGridApiRef()

  const handleDeleteSelected = (ids) => {
    dispatch(deleteSelectedItems(ids))
  }

  const handleDeleteItems = async () => {
    if (deleteConfirmationText === 'DELETE') {
      try {
        const idsString = selectedItems.filter(Boolean).join(',')
        await deleteKybListItems({ ids: idsString }).unwrap()
        setDeleteModalOpen(false)
        setDeleteConfirmationText('')
        dispatch(deleteSelectedItems([]))
        setIsLoading(true)
        toaster.success(t('messages:deleteSuccess'))
      } catch (error) {
        console.error('Error deleting items', error)
        toaster.error(t('messages:deleteError'))
      }
    }
  }

  const handleChangePage = (_, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10)
    setRowsPerPage(newRowsPerPage)
    setPage(0)
  }

  const columnsCnpj = useMemo(
    () => [
      {
        field: '_id',
        headerName: 'ID',
        flex: 1,  
        renderCell: (params) => {
          return <Typography variant="number">{params?.row?._id}</Typography>
        },
      },
      {
        field: 'documentNumber',
        headerName: t('common:documentNumber'),
        flex: 1,
        renderCell: (params) => {
          return <Typography variant="number">{params?.row?.documentNumber ?? '---'}</Typography>
        },
      },
      {
        field: 'name',
        headerName: t('common:name'),
        flex: 1,
        renderCell: (params) => {
          return (
            <Box display="flex" alignItems="center" gap=".5rem">
              {params?.row?.name && (
                <LetterAvatar fullName={params.row.name.toUpperCase()} height="1.5rem" width="1.5rem" />
              )}
              <p>{params.row.name}</p>
            </Box>
          )
        },
      },
      {
        field: 'address',
        headerName: t('common:address'),
        flex: 1,
        renderCell: (params) => {
          const address = params.row?.listDetails?.address
          // Check if address is undefined or contains 'undefined' string
          if (!address || address === 'undefined' || address.includes('undefined')) {
            return <p>---</p>
          }
          return <p>{address}</p>
        },
      },
      {
        field: 'relationship',
        headerName: t('common:relationship'),
        flex: 1,
        renderCell: (params) => {
          return <p>{params.row?.listDetails?.relationship ?? '---'}</p>
        },
      },
      {
        field: 'expirationDate',
        headerName: t('common:expirationDate'),
        flex: 1,
        renderCell: (params) => {
          return <p>{params.row?.listDetails?.expirationDate ?? '---'}</p>
        },
      },
      {
        field: 'phone',
        headerName: t('common:phone'),
        flex: 1,
        renderCell: (params) => {
          return <p>{params.row?.listDetails?.phone ?? '---'}</p>
        },
      },
      {
        field: 'anotherField',
        headerName: t('common:anotherField'),
        flex: 1,
        renderCell: (params) => {
          const value = params.row?.listDetails?.anotherField
          if (!value) return <p>---</p>
          return (
            <Box sx={{ whiteSpace: 'pre-wrap' }}>
              {value.split(',').map((item, index) => (
                <span key={index}>
                  {item.trim()}
                  {index < value.split(',').length - 1 && <br />}
                </span>
              ))}
            </Box>
          )
        },
      },
      {
        field: 'description',
        headerName: t('common:description'),
        flex: 1,
        renderCell: (params) => {
          return <p>{params.row?.listDetails?.description ?? '---'}</p>
        },
      },
    ],
    [],
  )

  const columnsCpf = useMemo(
    () => [
      {
        field: '_id',
        headerName: 'ID',
        flex: 1,
        renderCell: (params) => {
          return <Typography variant="number">{params?.row?._id}</Typography>
        },
      },
      {
        field: 'documentNumber',
        headerName: t('common:documentNumber'),
        flex: 1,
        renderCell: (params) => {
          return <Typography variant="number">{params?.row?.documentNumber ?? '---'}</Typography>
        },
      },
      {
        field: 'name',
        headerName: t('common:name'),
        flex: 1,
        renderCell: (params) => {
          return <p>{params.row.name ?? '---'}</p>
        },
      },
      {
        field: 'relatedCompany',
        headerName: t('common:relatedCompany'),
        flex: 1,
        renderCell: (params) => {
          return <p>{params.row?.listDetails?.relatedCompany ?? '---'}</p>
        },
      },
      {
        field: 'relationship',
        headerName: t('common:relationship'),
        flex: 1,
        renderCell: (params) => {
          return <p>{params.row?.listDetails?.relationship ?? '---'}</p>
        },
      },
      {
        field: 'isPep',
        headerName: 'Is Pep',
        flex: 1,
        renderCell: (params) => {
          return <p>{params.row?.listDetails?.isPep ?? '---'}</p>
        },
      },
      {
        field: 'riskLevel',
        headerName: t('common:riskLevel'),
        flex: 1,
        renderCell: (params) => {
          const riskLevel = params.row?.listDetails?.riskLevel
          const validRiskLevels = ['VERY_HIGH', 'HIGH', 'MEDIUM', 'LOW', 'VERY_LOW', 'NOT_DEFINED']
          const phRiskLevel =
            riskLevel && validRiskLevels.includes(riskLevel.toUpperCase()) ? riskLevel.toUpperCase() : 'NOT_DEFINED'
          return <RiskLevel type={phRiskLevel} />
        },
      },
      {
        field: 'phone',
        headerName: t('common:phone'),
        flex: 1,
        renderCell: (params) => {
          return <p>{params.row?.listDetails?.phone ?? '---'}</p>
        },
      },
      {
        field: 'anotherField',
        headerName: t('common:anotherField'),
        flex: 1,
        renderCell: (params) => {
          const value = params.row?.listDetails?.anotherField
          if (!value) return <p>---</p>
          return (
            <Box sx={{ whiteSpace: 'pre-wrap' }}>
              {value.split(',').map((item, index) => (
                <span key={index}>
                  {item.trim()}
                  {index < value.split(',').length - 1 && <br />}
                </span>
              ))}
            </Box>
          )
        },
      },
      {
        field: 'description',
        headerName: t('common:description'),
        flex: 1,
        renderCell: (params) => {
          return <p>{params.row?.listDetails?.description ?? '---'}</p>
        },
      },
    ],
    [],
  )

  const handleResetButton = () => {
    setId('')
    setStatus('')
    setName('')
    setDocumentNumber('')
    setSelectedDate(null)
    setPage(0)
    setIsLoading(true)

    return dispatch(
      updateKybListFilters({
        page: 0,
        limit: 25,
        offset: 0,
        id: '',
        status: null,
        name: '',
        fromDate: null,
        toDate: '',
        documentNumber: '',
      }),
    )
  }

  const handleDispatch = () => {
    const selectedStartOfDay = selectedDate ? dayjs(selectedDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss') : null
    const todayEndOfDay = toDate ? dayjs(toDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss') : null
    setIsLoading(true)
    return dispatch(
      updateKybListFilters({
        limit: rowsPerPage,
        offset: rowsPerPage * page,
        page,
        id,
        status,
        name,
        ...(selectedDate && { fromDate: selectedStartOfDay }),
        toDate: todayEndOfDay,
        documentNumber,
      }),
    )
  }

  const handleChange = (event, newValue) => {
    handleResetButton()
    navigate(`/kyb/lists/${newValue}`)
  }

  useEffect(() => {
    handleDispatch()
  }, [rowsPerPage, page])

  useEffect(() => {
    if (selectedDate) {
      const formattedDate = new Date(selectedDate)
      setToDate(selectedDate ? formattedDate.setDate(formattedDate.getDate()) : null)
    }
  }, [selectedDate])

  useEffect(() => {
    if (!tab || (tab !== 'CNPJ' && tab !== 'CPF')) {
      navigate('/kyb/lists/CNPJ')
      setActualTab('CNPJ')
    } else {
      setActualTab(tab)
    }
  }, [tab])

const handleUpdate = async () => {
  if (file !== '') {
    if (replaceLists) {
      setConfirmModalOpen(true)
    } else {
      await executeUpdate()
    }
  }
}

const executeUpdate = async () => {
  try {
    await updateKybList({ file, replaceLists }).unwrap()
    setModalOpen(false)
    setConfirmModalOpen(false)
    setDeleteConfirmation('')
    setFile('')
    setReplaceLists(false)
    setIsLoading(true)
    toaster.info(t('messages:updateSuccess'))
  } catch (err) {
    console.error('CODE ERROR: ', err.data.message)
    toaster.error(t(`messages:${err.data.message || "unexpectedErrorValues"}`))
  }
}

const handleConfirmReplace = async () => {
  if (deleteConfirmation === 'DELETE') {
    await executeUpdate()
  }
}

  return (
    <Box className={`${isLoading && 'spinner-transition'} filter`}>
      {isLoading && <Spinner noTransform />}
      <Box display="flex" justifyContent="space-between">
        <Box>
          <Box className="component-title">
            <Typography variant="h2">{t('messages:kyb.menu.blockedClients')}</Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Button size="small" onClick={() => setModalOpen(true)} variant="contained">
            {t('common:update')}
          </Button>
          <Button size="small" variant="contained">
            <a
              href={`${process.env.PUBLIC_URL}/exampleBlockList.xlsx`}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              {t('common:downloadExample')}
            </a>
          </Button>
        </Box>
      </Box>
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <TabContext value={actualTab}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Button 
              variant="label" 
              endIcon={<FilterList />} 
              onClick={() => setFilterModalOpen(true)}
              sx={{ mb: 1 }}
            >
              {t('common:filters')}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <TabList onChange={handleChange}>
              <Tab label="CNPJ" value="CNPJ" />
              <Tab label="CPF" value="CPF" />
            </TabList>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2">
                {t('messages:compliance.cases.selected.items', { items: selectedItems.length })}
              </Typography>
              <IconButton
                title={t('common:showHideColumns')}
                onClick={() => apiRef.current.showPreferences('columns')}
                sx={{
                  color: '#00C43D',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <Visibility fontSize="medium" />
              </IconButton>
              <IconButton
                title={t('common:delete')}
                disabled={selectedItems.length === 0}
                sx={{
                  color: selectedItems.length === 0 ? 'grey.500' : '#00C43D',
                }}
                onClick={() => setDeleteModalOpen(true)}
              >
                <Delete />
              </IconButton>
            </Box>
          </Box>

          <TabPanel value="CNPJ">
            <Box sx={{ width: '100%' }}>
              <TablePagination
                component="div"
                count={list?.pagination?.totalElements || 0}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                showFirstButton
                showLastButton
              />
              <DataGridPro
                getRowHeight={() => 'auto'}
                getRowId={(row) => row._id || row.id}
                rows={Array.isArray(list?.data) && list?.data?.length ? list?.data : []}
                columns={columnsCnpj}
                apiRef={apiRef}
                slots={{
                  columnMenu: CustomColumnMenu,
                  loadingOverlay: CustomLoadingOverlay,
                }}
                hideFooter={true}
                autoHeight
                loading={isLoading}
                checkboxSelection
                keepNonExistentRowsSelected
                onRowSelectionModelChange={handleDeleteSelected}
                rowSelectionModel={selectedItems}
              />
            </Box>
          </TabPanel>
          <TabPanel value="CPF">
            <Box sx={{ width: '100%' }}>
              <TablePagination
                component="div"
                count={list?.pagination?.totalElements || 0}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                showFirstButton
                showLastButton
              />
              <DataGridPro
                getRowHeight={() => 'auto'}
                getRowId={(row) => row._id || row.id}
                rows={Array.isArray(list?.data) && list?.data?.length ? list?.data : []}
                columns={columnsCpf}
                apiRef={apiRef}
                slots={{
                  columnMenu: CustomColumnMenu,
                  loadingOverlay: CustomLoadingOverlay,
                }}
                hideFooter={true}
                autoHeight
                loading={isLoading}
                checkboxSelection
                keepNonExistentRowsSelected
                onRowSelectionModelChange={handleDeleteSelected}
                rowSelectionModel={selectedItems}
              />
            </Box>
          </TabPanel>
        </TabContext>
      </Paper>
      <FilterModal
        modalOpen={filterModalOpen}
        setModalOpen={setFilterModalOpen}
        handleResetButton={handleResetButton}
        setPage={setPage}
        handleDispatch={handleDispatch}
      >
        <Box className="modal-box-filters">
          <Box className="modal-box">
            <Typography variant="subtitle3">
              {t('common:by')} {t('common:date')}:
            </Typography>

            <MyDatePicker selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
          </Box>

          <Box className="modal-box">
            <Typography variant="subtitle3">{t('common:by')} ID:</Typography>
            <TextField
              variant="outlined"
              size="small"
              value={id}
              onChange={(event) => setId(event.target.value)}
              placeholder={t('common:searchById')}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    onClick={(e) => {
                      e.preventDefault()
                      handleDispatch()
                    }}
                  >
                    <SearchOutlined />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box className="modal-box">
            <Typography variant="subtitle3">
              {t('common:by')} {t('common:documentNumber')}:
            </Typography>
            <TextField
              variant="outlined"
              size="small"
              value={documentNumber}
              onChange={(event) => setDocumentNumber(event.target.value)}
              placeholder={t('common:searchByDocument')}
            />
          </Box>
          <Box className="modal-box">
            <Typography variant="subtitle3">
              {t('common:by')} {t('common:name')}:
            </Typography>
            <TextField
              variant="outlined"
              size="small"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t('common:searchByName')}
            />
          </Box>
        </Box>
      </FilterModal>
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="modal" gap="1.5rem">
          <Box className="modal-top">
            <Typography variant="title">
              {t('common:update')} {t('common:blacklist')?.toLowerCase()} {t('common:data')?.toLowerCase()}
            </Typography>
          </Box>
          <Box className="modal-box-lists" padding="0">
            <Box className="lists-box">
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                <Typography variant="title2" sx={{ width: '100%', textAlign: 'center', marginBottom: '1rem' }}>
                  {t('common:addExcelFileForData')}
                </Typography>
              </Box>
              <Box className="draggable-main">
                <FileUploader handleChange={setFile} name="file" types={FILE_TYPES} />
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={replaceLists}
                    onChange={(e) => setReplaceLists(e.target.checked)}
                  />
                }
                label={t('messages:replaceListWithImported')}
                sx={{ marginTop: '1rem' }}
              />
            </Box>
          </Box>
          <Box className="lists-buttons">
            <Button
              size="small"
              variant="outlinedGrey"
              onClick={() => setModalOpen(false)}
            >
              {t('common:cancel')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleUpdate()}
            >
              {t('common:update')}
            </Button>
          </Box>
        </Box>
      </Modal>
      
      <Modal
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        aria-labelledby="confirm-modal-title"
        aria-describedby="confirm-modal-description"
      >
        <Box className="modal" gap="1.5rem">
          <Box className="modal-top">
            <Typography variant="title">
              {t('messages:confirmReplaceList')}
            </Typography>
          </Box>
          <Box className="modal-box-lists" padding="0">
            <Box className="lists-box">
              <Typography variant="body1" sx={{ textAlign: 'center', marginBottom: '1rem' }}>
                {t('messages:replaceListWarning')}
              </Typography>
              <Typography variant="body2" sx={{ textAlign: 'center', marginBottom: '1rem' }}>
                {t('messages:actionIrreversible')}
              </Typography>
              <Typography variant="body2" sx={{ textAlign: 'center', marginBottom: '1rem' }}>
                {t('messages:typeDeleteToConfirm')}
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="DELETE"
                sx={{ textAlign: 'center' }}
              />
            </Box>
          </Box>
          <Box className="lists-buttons">
            <Button
              size="small"
              variant="outlinedGrey"
              onClick={() => {
                setConfirmModalOpen(false)
                setDeleteConfirmation('')
              }}
            >
              {t('common:cancel')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleConfirmReplace()}
              disabled={deleteConfirmation !== 'DELETE'}
              color="error"
            >
              {t('common:update')}
            </Button>
          </Box>
        </Box>
      </Modal>
    
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        aria-labelledby="delete-modal-title"
        aria-describedby="delete-modal-description"
      >
        <Box className="modal" gap="1.5rem">
          <Box className="modal-top">
            <Typography variant="title">
              {t('messages:confirmDeleteItems')}
            </Typography>
          </Box>
          <Box className="modal-box-lists" padding="0">
            <Box className="lists-box">
              <Typography variant="body1" sx={{ textAlign: 'center', marginBottom: '1rem' }}>
                {t('messages:deleteItemsWarning', { count: selectedItems.length })}
              </Typography>
              <Typography variant="body2" sx={{ textAlign: 'center', marginBottom: '1rem' }}>
                {t('messages:actionIrreversible')}
              </Typography>
              <Typography variant="body2" sx={{ textAlign: 'center', marginBottom: '1rem' }}>
                {t('messages:typeDeleteToConfirm')}
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={deleteConfirmationText}
                onChange={(e) => setDeleteConfirmationText(e.target.value)}
                placeholder="DELETE"
                sx={{ textAlign: 'center' }}
              />
            </Box>
          </Box>
          <Box className="lists-buttons">
            <Button
              size="small"
              variant="outlinedGrey"
              onClick={() => {
                setDeleteModalOpen(false)
                setDeleteConfirmationText('')
              }}
            >
              {t('common:cancel')}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => handleDeleteItems()}
              disabled={deleteConfirmationText !== 'DELETE' || isDeleting}
              color="error"
            >
              {isDeleting ? 
              <>
              <Spinner size={20} color="inherit" />
              {t('common:delete')}
              </> 
              : t('common:delete')}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  )
}

export default KybLists
